import { FC, useEffect, useState } from 'react';
import FormTemplate from '../../../../components/FormComponents/FormTemplate/FormTemplate';
import InfoText from '../../../../components/InfoText/infoText';
import RadioGroup from '../../../../components/RadioGroup/RadioGroup';
import { RadioGroupOption } from '../../../../components/RadioGroup/RadioGroup.types';
import { NavigationAction } from '../../../../routing/StateMachineTypes';
import { TrackingClickEventData, TrackingElementSmoker } from '../../../../tracking/tracking.types';
import { AemFragmentCatalog } from '../../../../types/AemFragmentCatalog';
import { TemplateLayout } from '../../../../types/FormTemplate.types';
import { createLivingConditionsSmokerSchema } from '../../../../validation/LivingConditionsPage';
import { SmokerServerValues } from './SmokerForm.types';
import './SmokerForm.css';

export const smokerFormTemplateLayout: TemplateLayout<object> = {
  title: 'Geben Sie an, ob Sie rauchen.',
  footerInfoSection: () => (
    <InfoText textBeforeLink="Sie möchten jemand anders absichern? Dann geben Sie bitte an, ob diese Person Raucher oder Nichtraucher ist." />
  ),
};

const RADIO_GROUP_OPTIONS: RadioGroupOption[] = [
  {
    title: 'Nichtraucher/-in',
    value: SmokerServerValues.NON_SMOKER_MORE_THAN_10,
    description: 'noch nie oder seit mind. 10 Jahren nicht mehr geraucht',
  },
  {
    title: 'Nichtraucher/-in ',
    value: SmokerServerValues.NON_SMOKER_AT_LEAST_A_YEAR,
    description: 'seit mind. 1 Jahr nicht mehr geraucht',
  },
  {
    title: 'Raucher/-in',
    value: SmokerServerValues.SMOKER,
    description: 'innerhalb des letzten Jahres geraucht',
  },
];

const SmokerMapper = {
  [SmokerServerValues.NON_SMOKER_MORE_THAN_10]: TrackingElementSmoker.RADIOBUTTON_NONSMOKERMIN10,
  [SmokerServerValues.NON_SMOKER_AT_LEAST_A_YEAR]:
    TrackingElementSmoker.RADIOBUTTON_NONSMOKERLESS10,
  [SmokerServerValues.SMOKER]: TrackingElementSmoker.RADIOBUTTON_SMOKER,
};

interface SmokerFormValues {
  isSmoker?: SmokerServerValues;
}

interface SmokerProps {
  smoker?: string;
  onSubmit: (value: string, navAction: NavigationAction) => void;
  onNavigateNoSubmit?: (navigationRequest: NavigationAction) => void;
  trackClick: (
    trackingData: TrackingClickEventData,
    smoker: SmokerServerValues,
    isSmoker: boolean,
  ) => void;
}

const SmokerForm: FC<SmokerProps> = ({ smoker, onSubmit, onNavigateNoSubmit, trackClick }) => {
  const [virtualAssistantFragment, setVirtualAssistantFragment] = useState<
    AemFragmentCatalog | undefined
  >(AemFragmentCatalog.STEP7VA);
  useEffect(() => {
    if (smoker === SmokerServerValues.SMOKER) {
      // Set VirtualAssistantText for smoker scenario
      setVirtualAssistantFragment(AemFragmentCatalog.STEP7VA1);
    } else {
      // Set VirtualAssistantText for no smoker scenario
      setVirtualAssistantFragment(AemFragmentCatalog.STEP7VA2);
    }
  }, []);

  const templateLayout = {
    ...smokerFormTemplateLayout,
    virtualAssistantFragment,
  };

  return (
    <div className="container__smoker">
      <FormTemplate<SmokerFormValues>
        templateLayout={templateLayout}
        formValues={{ isSmoker: smoker as SmokerServerValues }}
        validationSchema={createLivingConditionsSmokerSchema()}
        handleOnSubmitAction={(value: SmokerFormValues, navAction) => {
          if (value.isSmoker) {
            onSubmit(value.isSmoker, navAction);
          }
          return;
        }}
        handleNavigateNoSubmitAction={onNavigateNoSubmit}
        render={[
          {
            name: 'isSmoker',
            component: (form, field) => (
              <RadioGroup
                radioCircleLeft={false}
                maxRow={3}
                options={RADIO_GROUP_OPTIONS}
                name="isSmoker"
                selectedValue={field.value}
                onChangeSelection={(_, value) => {
                  setVirtualAssistantFragment(
                    value === SmokerServerValues.SMOKER
                      ? AemFragmentCatalog.STEP7VA1
                      : AemFragmentCatalog.STEP7VA2,
                  );
                  form.setFieldValue(field.name, value);
                  trackClick(
                    { clickedElement: SmokerMapper[value as SmokerServerValues] },
                    value as SmokerServerValues,
                    value === SmokerServerValues.SMOKER,
                  );
                }}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default SmokerForm;
